import Raven from 'raven-js';
import { useLocation, useParams } from 'quartz-routing/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { NavMarker } from 'react-rhumb';

// @ts-ignore image
import certBackgroundShapeUrl from 'bender-url!../../images/blob.svg';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import formatName from 'I18n/utils/formatName';
import { SocialBlockFacebookIcon } from 'foundations-assets/icons/components/SocialBlockFacebookIcon';
import { SocialBlockLinkIcon } from 'foundations-assets/icons/components/SocialBlockLinkIcon';
import { SocialBlockLinkedinIcon } from 'foundations-assets/icons/components/SocialBlockLinkedinIcon';
import { SocialBlockXIcon } from 'foundations-assets/icons/components/SocialBlockXIcon';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { CopyButton } from 'foundations-components/transitional/components/CopyButton';
import { CopyTextArea } from 'foundations-components/transitional/components/CopyTextArea';
import { EditorPage } from 'foundations-components/transitional/components/EditorPage';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { H1 } from 'foundations-components/transitional/components/H1';
import { H2 } from 'foundations-components/transitional/components/H2';
import { LoadingOverlay } from 'foundations-components/transitional/components/LoadingOverlay';
import { SelectableButton } from 'foundations-components/transitional/components/SelectableButton';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { Toggle } from 'foundations-components/transitional/components/Toggle';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { View } from 'foundations-components/transitional/components/View';
import styled, { css } from 'foundations-theming/utils';
import { Button } from 'foundations-components/transitional/components/Button';
import AchievementPreviewModal from './AchievementPreviewModal';
import ApiError from 'academy-common-lib/components/error/ApiError';
import { AWARD_SHARE_BACK_PARAM, AWARD_SHARE_CODE_PARAM, AWARD_SHARE_TITLE_PARAM } from 'academy-common-lib/constants/AwardShareConsts';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import { useGetCertificateQuery } from '../../__generated__/chirp/com/hubspot/learning/center/credentials/rpc/MyTeamChirpService.hooks';
import AwardShareHeader from '../features/achievement/components/AwardShareHeader';
import getLang from '../utils/getLang';
import ImageLoader from './ImageLoader';
import UsageTracker from '../utils/UsageTracker';
import { ACADEMY_LINKEDIN_ORG_ID, SOCIAL_LINKS } from '../constants/CourseConstants.migrated';
import { useUserInfo } from '../features/accounts';
import { usePublicAchievement } from '../features/achievement';
import { useAchievementVisibilityToggle, useCredentialDefinition, useMaybeCreateAchievementByCredential } from '../features/achievement/credentialsQueries';
import { useFullChirpTrack } from '../features/tracks/tracksQueries.migrated';
import { SendToUpwork } from '../features/upwork';
import { cleanTrackingProps, toAwardType } from '../utils/awardTrackingUtils';
import { copyToClipboard } from '../utils/copyToClipboard';
import { addUrlParams, getPublicAchievementUrl } from '../utils/RoutingUtils.migrated';
import { slugify } from '../utils/utils.migrated';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const CertificateBackground = styled.div.withConfig({
  displayName: "AchievementShareControls__CertificateBackground"
})(["display:flex;justify-content:center;padding:", ";background-image:", ";background-position:top;background-repeat:no-repeat;background-size:100% 100%;width:100%;", ""], ({
  theme
}) => theme.space['1000'], ({
  src
}) => src ? `url(${src})` : 'none', ({
  $isNotMobile,
  theme
}) => $isNotMobile && css(["padding:", ";"], theme.space['600']));
const GridContainer = styled.div.withConfig({
  displayName: "AchievementShareControls__GridContainer"
})(["display:grid;grid-template-columns:1fr;grid-template-areas:'heading' 'consent' 'preview';", ""], ({
  $isNotMobile,
  theme
}) => $isNotMobile && css(["grid-template-columns:2fr 1fr;grid-template-areas:'heading preview' 'consent preview';column-gap:", ";"], theme.space['1300']));
const Heading = styled.div.withConfig({
  displayName: "AchievementShareControls__Heading"
})(["grid-area:heading;display:flex;align-items:end;"]);
const ShareConsent = styled.div.withConfig({
  displayName: "AchievementShareControls__ShareConsent"
})(["grid-area:consent;"]);
const Preview = styled.div.withConfig({
  displayName: "AchievementShareControls__Preview"
})(["grid-area:preview;"]);
const ButtonGrid = styled.div.withConfig({
  displayName: "AchievementShareControls__ButtonGrid"
})(["display:grid;gap:", ";grid-template-columns:repeat(1,1fr);", " ", ""], ({
  theme
}) => theme.space['300'], ({
  $isNotMobile
}) => $isNotMobile && css(["grid-template-columns:repeat(2,1fr);"]), ({
  $isNotLaptop
}) => $isNotLaptop && css(["grid-template-columns:repeat(5,1fr);"]));

// --- Hooks ---

const useAchievementSocialLinks = (achievement, credentialName) => {
  const {
    userInfo
  } = useUserInfo();
  const {
    publicAchievement
  } = usePublicAchievement(achievement === null || achievement === void 0 ? void 0 : achievement.publicId);
  const achievementName = credentialName !== null && credentialName !== void 0 ? credentialName : I18n.text('learningCenter.awardShareControls.defaultAchievementName');
  const language = publicAchievement !== null && publicAchievement !== void 0 && publicAchievement.language ? String(publicAchievement.language).toLowerCase() : 'en';
  const urlSlug = useMemo(() => {
    if (!userInfo) return '';
    const {
      firstName,
      lastName
    } = userInfo.user;
    const userSlug = slugify(formatName({
      firstName,
      lastName
    })) || 'user';
    const nameSlug = slugify(achievementName) || 'achievement';
    return `${userSlug}/${nameSlug}`;
  }, [userInfo, achievementName]);

  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const achievementUrl = useMemo(() => {
    if (!(achievement !== null && achievement !== void 0 && achievement.publicId)) return '';
    return window.location.href.replace(/\/academy\/\d+\/.*$/, getPublicAchievementUrl(achievement.publicId, language, urlSlug));
  }, [achievement === null || achievement === void 0 ? void 0 : achievement.publicId, language, urlSlug]);
  return useMemo(() => {
    if (!achievementUrl || !achievement) return null;
    const shareText = I18n.text('learningCenter.awardShareControls.achievementShareText', {
      achievementName
    });
    const facebookUrl = encodeURI(`${SOCIAL_LINKS.facebook_share}?u=${achievementUrl}&quote=${shareText}`);
    const xUrl = encodeURI(`${SOCIAL_LINKS.x_share}?text=${shareText} ${achievementUrl}`);
    const linkedinUrl = encodeURI(`${SOCIAL_LINKS.linkedin_share}?url=${achievementUrl}`);
    const linkedinAddToProfile = addUrlParams(SOCIAL_LINKS.linkedin_add_to_profile, {
      name: achievementName,
      organizationId: ACADEMY_LINKEDIN_ORG_ID,
      certUrl: achievementUrl,
      certId: achievement.publicId
    });
    return {
      facebook: facebookUrl,
      x: xUrl,
      linkedin: linkedinUrl,
      linkedinAddToProfile,
      achievementUrl
    };
  }, [achievementUrl, achievement, achievementName]);
};

// --- Social Share Buttons (inline) ---

const socialShareButtonDefinitions = [{
  networkName: 'facebook',
  hrefKey: 'facebook',
  icon: /*#__PURE__*/_jsx(SocialBlockFacebookIcon, {
    color: "do-not-use-facebook",
    size: "1000"
  }),
  buttonText: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "learningCenter.awardShareControls.facebook"
  })
}, {
  networkName: 'linkedin',
  hrefKey: 'linkedin',
  icon: /*#__PURE__*/_jsx(SocialBlockLinkedinIcon, {
    color: "do-not-use-linkedin",
    size: "1000"
  }),
  buttonText: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "learningCenter.awardShareControls.linkedinPost"
  })
}, {
  networkName: 'linkedin',
  hrefKey: 'linkedinAddToProfile',
  icon: /*#__PURE__*/_jsx(SocialBlockLinkedinIcon, {
    color: "do-not-use-linkedin",
    size: "1000"
  }),
  buttonText: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "learningCenter.awardShareControls.linkedinLicenses"
  })
}, {
  networkName: 'x',
  hrefKey: 'x',
  icon: /*#__PURE__*/_jsx(SocialBlockXIcon, {
    color: "do-not-use-x",
    size: "1000"
  }),
  buttonText: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "learningCenter.awardShareControls.xSocialNetwork"
  })
}];

/**
 * `?backLocation=` is user-supplied, and the value ends up both as an anchor
 * href and as a `navigate` target. Only accept a same-origin relative path:
 * `//host` and `/\host` are protocol-relative and would leave the app.
 */
const getSafeBackPath = path => path && /^\/(?![/\\])/.test(path) ? path : undefined;

// --- Main Component ---

const AchievementShareControls = () => {
  var _searchParams$get, _location$state, _searchParams$get2, _location$state2, _ref, _publicAchievement$ba, _ref2, _getSafeBackPath, _location$state3;
  const {
    isMobile,
    isLaptop,
    isTablet,
    isNotMobile,
    isNotLaptop
  } = useMediaQuery();
  const location = useLocation();
  const {
    grantedCredentialId: grantedCredentialIdParam
  } = useParams();
  const grantedCredentialId = Number(grantedCredentialIdParam);
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const grantedCredentialCode = (_searchParams$get = searchParams.get(AWARD_SHARE_CODE_PARAM)) !== null && _searchParams$get !== void 0 ? _searchParams$get : (_location$state = location.state) === null || _location$state === void 0 ? void 0 : _location$state.code;
  const titleFromRouteState = (_searchParams$get2 = searchParams.get(AWARD_SHARE_TITLE_PARAM)) !== null && _searchParams$get2 !== void 0 ? _searchParams$get2 : (_location$state2 = location.state) === null || _location$state2 === void 0 ? void 0 : _location$state2.title;
  const {
    achievement,
    achievementsLoading,
    achievementsError,
    isCreatingAchievement,
    createAchievementError
  } = useMaybeCreateAchievementByCredential(grantedCredentialId, grantedCredentialCode);
  const {
    publicAchievement
  } = usePublicAchievement(achievement === null || achievement === void 0 ? void 0 : achievement.publicId);
  const {
    icon: credentialDefinitionIcon,
    name: credentialDefinitionName
  } = useCredentialDefinition(achievement === null || achievement === void 0 ? void 0 : achievement.credentialDefinitionId);
  const {
    isPrivate,
    isDisabled,
    toggleVisibility,
    updateLoading
  } = useAchievementVisibilityToggle(achievement);
  const credentialName = (achievement === null || achievement === void 0 ? void 0 : achievement.credentialDefinitionName) || credentialDefinitionName || titleFromRouteState || '';
  const shareLinks = useAchievementSocialLinks(achievement, credentialName);
  const isBadge = (achievement === null || achievement === void 0 ? void 0 : achievement.credentialType) === 'BADGE';
  const isCertification = (achievement === null || achievement === void 0 ? void 0 : achievement.credentialType) === 'CERTIFICATION';
  const {
    data: certificateData,
    loading: certificateLoading
  } = useGetCertificateQuery({
    variables: {
      grantedCredentialId
    },
    skip: !isCertification || !grantedCredentialId
  });
  const currentLang = getLang().toUpperCase();
  const linkedTrackId = useMemo(() => {
    var _mappings$find$trackI, _mappings$find, _mappings$;
    if (!isCertification) return undefined;
    const mappings = achievement === null || achievement === void 0 ? void 0 : achievement.trackCredentialMappings;
    if (!mappings || mappings.length === 0) return undefined;
    return (_mappings$find$trackI = (_mappings$find = mappings.find(m => String(m.language) === currentLang)) === null || _mappings$find === void 0 ? void 0 : _mappings$find.trackId) !== null && _mappings$find$trackI !== void 0 ? _mappings$find$trackI : (_mappings$ = mappings[0]) === null || _mappings$ === void 0 ? void 0 : _mappings$.trackId;
  }, [isCertification, achievement === null || achievement === void 0 ? void 0 : achievement.trackCredentialMappings, currentLang]);
  const {
    track: linkedTrack
  } = useFullChirpTrack(linkedTrackId !== null && linkedTrackId !== void 0 ? linkedTrackId : 0);
  const anyLoading = achievementsLoading || certificateLoading || isCreatingAchievement;
  const achievementName = credentialName;
  const achievementType = toAwardType(achievement === null || achievement === void 0 ? void 0 : achievement.credentialType);
  const certificateImageUrl = isCertification && certificateData !== null && certificateData !== void 0 && certificateData.certificateUrl ? certificateData.certificateUrl : undefined;
  const previewImageUrl = (_ref = certificateImageUrl !== null && certificateImageUrl !== void 0 ? certificateImageUrl : publicAchievement === null || publicAchievement === void 0 ? void 0 : publicAchievement.badge) !== null && _ref !== void 0 ? _ref : credentialDefinitionIcon;
  const embedImageUrl = (_publicAchievement$ba = publicAchievement === null || publicAchievement === void 0 ? void 0 : publicAchievement.badge) !== null && _publicAchievement$ba !== void 0 ? _publicAchievement$ba : credentialDefinitionIcon;
  const [previewCredentialId, setPreviewCredentialId] = useState(null);
  const backPath = (_ref2 = (_getSafeBackPath = getSafeBackPath(searchParams.get(AWARD_SHARE_BACK_PARAM))) !== null && _getSafeBackPath !== void 0 ? _getSafeBackPath : (_location$state3 = location.state) === null || _location$state3 === void 0 || (_location$state3 = _location$state3.backFromAwardShare) === null || _location$state3 === void 0 ? void 0 : _location$state3.pathname) !== null && _ref2 !== void 0 ? _ref2 : '/profile';
  useEffect(() => {
    if (achievement && achievementName) {
      UsageTracker.trackStandalone('Award Interaction', cleanTrackingProps({
        action: 'view-sharing-controls',
        type: achievementType,
        name: achievementName,
        itemId: grantedCredentialId.toString(),
        view: 'achievement-sharing-controls'
      }));
    }
  }, [achievement, achievementName, achievementType, grantedCredentialId]);
  const handleCopyAwardCode = useCallback(() => {
    UsageTracker.trackStandalone('Award Interaction', cleanTrackingProps({
      action: 'copy-embed-code',
      type: achievementType,
      name: achievementName,
      itemId: grantedCredentialId.toString(),
      view: 'achievement-sharing-controls'
    }));
  }, [achievementName, achievementType, grantedCredentialId]);
  const handleImageError = ({
    currentTarget
  }) => {
    Raven.captureMessage(`Achievements: error loading achievement image`, {
      extra: {
        src: currentTarget.src
      }
    });
  };
  const onSocialShareClick = useCallback((shareType, destinationUrl) => {
    if (destinationUrl) {
      window.open(destinationUrl, '_blank', 'noopener,noreferrer');
    }
    UsageTracker.trackStandalone('Award Interaction', cleanTrackingProps({
      action: 'social-share',
      type: achievementType,
      name: achievementName,
      itemId: grantedCredentialId.toString(),
      view: 'achievement-sharing-controls',
      shareType,
      destinationUrl: destinationUrl || ''
    }));
  }, [achievementName, achievementType, grantedCredentialId]);
  const onToggle = useCallback(({
    target: {
      checked
    }
  }) => {
    const privacy = checked ? 'PUBLIC' : 'PRIVATE';
    toggleVisibility();
    UsageTracker.trackStandalone('Award Interaction', cleanTrackingProps({
      action: 'toggle-privacy',
      type: achievementType,
      name: achievementName,
      itemId: grantedCredentialId.toString(),
      privacy
    }));
  }, [toggleVisibility, grantedCredentialId, achievementName, achievementType]);

  // --- Embed code ---
  const {
    userInfo
  } = useUserInfo();
  const urlSlug = useMemo(() => {
    if (!userInfo || !achievementName) return '';
    const {
      firstName,
      lastName
    } = userInfo.user;
    const userSlug = slugify(formatName({
      firstName,
      lastName
    })) || 'user';
    const nameSlug = slugify(achievementName) || 'achievement';
    return `${userSlug}/${nameSlug}`;
  }, [userInfo, achievementName]);
  const language = publicAchievement !== null && publicAchievement !== void 0 && publicAchievement.language ? String(publicAchievement.language).toLowerCase() : 'en';

  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const embedCode = useMemo(() => {
    if (!(achievement !== null && achievement !== void 0 && achievement.publicId) || !embedImageUrl) return '';
    const achievementPageUrl = window.location.href.replace(/\/academy\/\d+\/.*$/, getPublicAchievementUrl(achievement.publicId, language, urlSlug));
    return `<!-- Begin: HubSpot Academy - ${achievementName} Badge -->\n<div class='academy-badge'>\n<a href='${achievementPageUrl}' title='${achievementName}'>\n<img src='${embedImageUrl}' />\n</a>\n</div>\n<!-- End: HubSpot Academy - ${achievementName} Badge -->`;
  }, [achievement === null || achievement === void 0 ? void 0 : achievement.publicId, embedImageUrl, achievementName, language, urlSlug]);
  const isEmbedDisabled = !embedImageUrl || isPrivate;
  if (anyLoading) {
    return /*#__PURE__*/_jsx(LoadingOverlay, {
      "data-test-id": "loading-spinner"
    });
  }
  if (!achievement) {
    return /*#__PURE__*/_jsx(ApiError, {
      error: achievementsError !== null && achievementsError !== void 0 ? achievementsError : createAchievementError,
      marker403: "ACHIEVEMENT_SHARE_CONTROLS_FORBIDDEN",
      marker404: "ACHIEVEMENT_SHARE_CONTROLS_NOT_FOUND",
      markerDefault: "ACHIEVEMENT_SHARE_CONTROLS_ERROR",
      resetLink: backPath
    });
  }
  return /*#__PURE__*/_jsxs(NavMarker, {
    name: "ACHIEVEMENT_SHARE_CONTROLS_LOADED",
    children: [/*#__PURE__*/_jsx(EditorPage, {
      "data-test-id": "achievement-share-controls",
      headerComponent: /*#__PURE__*/_jsx(AwardShareHeader, {
        backLinkHref: backPath
      }),
      inset: "0",
      position: "absolute",
      zIndex: 5,
      children: /*#__PURE__*/_jsx(Flex, {
        justifyContent: "center",
        children: /*#__PURE__*/_jsxs(View, {
          marginBlock: isMobile ? '0' : '1000',
          marginInline: isLaptop ? '0' : '1100',
          maxInlineSize: "953px",
          children: [/*#__PURE__*/_jsxs(GridContainer, {
            $isNotMobile: isNotMobile,
            children: [/*#__PURE__*/_jsx(Heading, {
              children: /*#__PURE__*/_jsx(H1, {
                marginBlockEnd: isMobile ? '600' : '1100',
                typography: isMobile ? 'heading-400' : undefined,
                children: achievementName
              })
            }), /*#__PURE__*/_jsxs(ShareConsent, {
              children: [/*#__PURE__*/_jsx(H2, {
                typography: "heading-200",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "learningCenter.awardShareControls.shareHeading"
                })
              }), /*#__PURE__*/_jsxs(Flex, {
                gap: "500",
                children: [/*#__PURE__*/_jsxs(View, {
                  children: [/*#__PURE__*/_jsx(Text, {
                    paddingBlockEnd: "100",
                    typography: "heading-50",
                    children: /*#__PURE__*/_jsx(FormattedMessage, {
                      message: "learningCenter.awardShareControls.consentHeading"
                    })
                  }), /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "learningCenter.awardShareControls.sharingConsent"
                  })]
                }), /*#__PURE__*/_jsx(Flex, {
                  alignSelf: "center",
                  children: /*#__PURE__*/_jsx(Tooltip, {
                    title: isPrivate ? /*#__PURE__*/_jsx(FormattedMessage, {
                      message: "learningCenter.awardShareControls.disabledToggleTooltip"
                    }) : null,
                    use: "longform",
                    children: /*#__PURE__*/_jsx("div", {
                      "data-loading": updateLoading || achievementsLoading,
                      children: /*#__PURE__*/_jsx(Toggle, {
                        checked: !isPrivate,
                        "data-test-id": "achievement-visibility-toggle",
                        disabled: updateLoading || achievementsLoading,
                        onChange: onToggle,
                        size: "small"
                      })
                    })
                  })
                })]
              })]
            }), /*#__PURE__*/_jsx(Preview, {
              children: /*#__PURE__*/_jsx(Flex, {
                alignItems: "center",
                flexDirection: "column",
                marginBlockStart: isMobile ? '800' : '0',
                marginInline: isMobile ? '400' : '0',
                children: previewImageUrl ? /*#__PURE__*/_jsxs(_Fragment, {
                  children: [/*#__PURE__*/_jsx(CertificateBackground, {
                    $isNotMobile: isNotMobile,
                    src: certBackgroundShapeUrl,
                    children: /*#__PURE__*/_jsx(ImageLoader, {
                      alt: I18n.text('learningCenter.certification.altText'),
                      errorComponent: /*#__PURE__*/_jsx(TFlex, {
                        alignItems: "center",
                        blockSize: "100%",
                        justifyContent: "center",
                        children: /*#__PURE__*/_jsx(FormattedMessage, {
                          message: "learningCenter.certification.errors.imgLoadingError"
                        })
                      }),
                      inlineSize: isBadge ? '200px' : undefined,
                      onError: handleImageError,
                      src: previewImageUrl
                    })
                  }), !achievementsLoading && /*#__PURE__*/_jsx(Tooltip, {
                    title: isDisabled ? /*#__PURE__*/_jsx(FormattedMessage, {
                      message: "learningCenter.awardShareControls.disabledTooltip"
                    }) : undefined,
                    use: "longform",
                    children: /*#__PURE__*/_jsx(Button, {
                      "data-test-id": "open-achievement-preview",
                      disabled: !!achievementsError || isDisabled,
                      onClick: () => setPreviewCredentialId(grantedCredentialId),
                      use: "link",
                      children: /*#__PURE__*/_jsx(FormattedMessage, {
                        message: "learningCenter.awardShareControls.previewCert"
                      })
                    })
                  })]
                }) : /*#__PURE__*/_jsx(Text, {
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "learningCenter.certification.errors.imgLoadingError"
                  })
                })
              })
            })]
          }), /*#__PURE__*/_jsxs(Flex, {
            flexDirection: "column",
            marginBlockStart: "600",
            children: [/*#__PURE__*/_jsx(Text, {
              typography: "heading-50",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.awardShareControls.selectSharingOption"
              })
            }), /*#__PURE__*/_jsx(Text, {
              typography: isMobile ? 'detail-100' : undefined,
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.awardShareControls.consentMessage"
              })
            }), /*#__PURE__*/_jsx(Flex, {
              justifyContent: "center",
              marginBlockStart: "300",
              children: /*#__PURE__*/_jsxs(ButtonGrid, {
                $isNotLaptop: isNotLaptop,
                $isNotMobile: isNotMobile,
                children: [shareLinks && socialShareButtonDefinitions.map(({
                  networkName,
                  hrefKey,
                  icon,
                  buttonText
                }) => /*#__PURE__*/_jsx(Tooltip, {
                  title: isDisabled ? /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "learningCenter.awardShareControls.disabledTooltip"
                  }) : undefined,
                  use: "longform",
                  children: /*#__PURE__*/_jsx(SelectableButton, {
                    "data-test-id": "social-sharing-option",
                    "data-test-url": shareLinks[hrefKey],
                    disabled: isDisabled,
                    maxInlineSize: isLaptop ? '100%' : undefined,
                    onClick: () => onSocialShareClick(networkName, shareLinks[hrefKey]),
                    selected: false,
                    size: isLaptop ? 'sm' : 'lg',
                    title: buttonText,
                    children: icon
                  })
                }, `${networkName}-${hrefKey}`)), /*#__PURE__*/_jsx(SelectableButton, {
                  disabled: isDisabled,
                  maxInlineSize: isLaptop ? '100%' : undefined,
                  onClick: async () => {
                    try {
                      if (shareLinks !== null && shareLinks !== void 0 && shareLinks.achievementUrl) {
                        await copyToClipboard(shareLinks === null || shareLinks === void 0 ? void 0 : shareLinks.achievementUrl);
                        FloatingAlertStore.addAlert({
                          message: /*#__PURE__*/_jsx(FormattedMessage, {
                            message: "learningCenter.awardShareControls.linkCopied"
                          }),
                          type: 'success',
                          closeable: false,
                          timeout: 2000
                        });
                      }
                      onSocialShareClick('copy-link');
                    } catch (error) {
                      console.error('Failed to copy to clipboard:', error);
                    }
                  },
                  selected: false,
                  size: isLaptop ? 'sm' : 'lg',
                  title: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "learningCenter.awardShareControls.copyLink"
                  }),
                  children: /*#__PURE__*/_jsx(SocialBlockLinkIcon, {
                    color: "icon-core-subtle",
                    size: "1000"
                  })
                })]
              })
            })]
          }), /*#__PURE__*/_jsxs(View, {
            as: "section",
            paddingBlockStart: "1000",
            children: [/*#__PURE__*/_jsxs(Flex, {
              flexDirection: isTablet ? 'column' : 'row',
              gap: "600",
              children: [/*#__PURE__*/_jsxs("div", {
                children: [/*#__PURE__*/_jsx(Text, {
                  as: "h2",
                  marginBlockEnd: "100",
                  typography: "heading-200",
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "learningCenter.embedAward.embedBadgeTitle"
                  })
                }), /*#__PURE__*/_jsx(Text, {
                  typography: "detail-200",
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "learningCenter.embedAward.embedBadgeToggleDescription"
                  })
                }), /*#__PURE__*/_jsx(Tooltip, {
                  title: isEmbedDisabled ? /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "learningCenter.awardShareControls.disabledTooltip"
                  }) : undefined,
                  use: "longform",
                  children: /*#__PURE__*/_jsx(CopyTextArea, {
                    alignItems: "center",
                    buttonRowRenderer: props => {
                      return /*#__PURE__*/_jsx(CopyButton, Object.assign({}, props, {
                        disabled: isEmbedDisabled,
                        hint: isEmbedDisabled ? () => /*#__PURE__*/_jsx(FormattedMessage, {
                          message: "learningCenter.embedAward.disabledCopyButtonHint"
                        }) : undefined,
                        onClick: handleCopyAwardCode,
                        children: /*#__PURE__*/_jsx(FormattedMessage, {
                          message: "learningCenter.embedAward.copyEmbed"
                        })
                      }));
                    },
                    buttonUse: "tertiary-light",
                    display: "flex",
                    flexDirection: isMobile ? 'column' : 'row',
                    flexWrap: "nowrap",
                    gap: "400",
                    marginBlockStart: "400",
                    textAreaProps: {
                      disabled: isEmbedDisabled,
                      minBlockSize: '40px',
                      maxBlockSize: '40px',
                      flexGrow: 1
                    },
                    use: "code",
                    value: embedCode
                  })
                })]
              }), embedImageUrl && /*#__PURE__*/_jsx(Flex, {
                alignItems: "end",
                justifyContent: "center",
                marginBlockEnd: isMobile ? '600' : '0',
                minInlineSize: "200px",
                children: /*#__PURE__*/_jsx(ImageLoader, {
                  alt: achievementName,
                  inlineSize: isBadge ? undefined : '195px',
                  maxInlineSize: isBadge ? '120px' : undefined,
                  onError: handleImageError,
                  src: embedImageUrl
                })
              })]
            }), isCertification && (linkedTrack === null || linkedTrack === void 0 ? void 0 : linkedTrack.upworkCode) && linkedTrackId && /*#__PURE__*/_jsx(Flex, {
              alignItems: "center",
              justifyContent: "center",
              margin: "auto",
              marginBlockStart: "600",
              children: /*#__PURE__*/_jsx(SendToUpwork, {
                trackId: linkedTrackId,
                trackName: linkedTrack.name
              })
            })]
          })]
        })
      })
    }), previewCredentialId !== null && /*#__PURE__*/_jsx(AchievementPreviewModal, {
      grantedCredentialId: previewCredentialId,
      onClose: () => setPreviewCredentialId(null)
    })]
  });
};
export default AchievementShareControls;